/**
 * 檔案用途區塊
 * @module owner-code-random-mapping
 * @purpose 維護業主代號與登入簽名顯示字串的亂數代碼對照，並提供 Wallet Connect 文案替換與顯示值決策函式。
 * @external https://innotech.atlassian.net/browse/IN-113493
 * @external https://innotech.atlassian.net/browse/IN-124251
 */

/**
 * 宣告內容用途說明與單號關聯
 * @description 業主代號對應亂數代碼表，作為未提供 OPS 顯示字串時的備援來源。
 * @purpose 統一管理 `window._env_.VENDERID` 對應的顯示代碼，供簽名文案組裝流程讀取。
 * @external https://innotech.atlassian.net/browse/IN-113493
 */
export const OWNER_CODE_RANDOM_MAPPING: Record<string, string> = {
  vd001: '8Xbet',
  vd002: '333',
  vd003: 'HG22',
  vd004: '6686',
  vd006: '8868',
  vd007: '1919',
  vd008: '1717',
  vd009: '2121',
  vd011: '2bet',
  vd012: '3377',
  vd013: 'VSbet',
  vd014: 'ByGame',
  vd015: '8Xbet',
  vd016: 'keybet',
  vd099: 'demo',
};

/**
 * 宣告內容用途說明與單號關聯
 * @description 文案中業主顯示字串的佔位符常數。
 * @purpose 集中定義 `{vendor}` 樣板字串，避免替換規則分散與字面值重複。
 * @external https://innotech.atlassian.net/browse/IN-113493
 */
const PLACEHOLDER = '{vendor}';

/**
 * 宣告內容用途說明與單號關聯
 * @description 依輸入業主代號回傳對應亂數代碼；若表中不存在則回退原始代號。
 * @purpose 在簽名顯示值流程中提供可容錯的代碼解析，確保未知代號仍有可顯示內容。
 * @external https://innotech.atlassian.net/browse/IN-113493
 */
export function getRandomCodeByOwnerCode(ownerCode: string): string {
  const normalized = ownerCode?.trim().toLowerCase() ?? '';
  return OWNER_CODE_RANDOM_MAPPING[normalized] ?? ownerCode ?? '';
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 以顯示文字取代翻譯字串中所有 `{vendor}` 佔位符。
 * @purpose 將已決策的業主顯示值注入最終簽名文案，支援多次出現的全域替換。
 * @external https://innotech.atlassian.net/browse/IN-124251
 */
export function replaceRandomCodePlaceholder(text: string, displayText: string): string {
  return text.replace(new RegExp(PLACEHOLDER.replace(/[{}]/g, '\\$&'), 'g'), displayText);
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 取得 Wallet Connect 簽名用顯示值，優先採用 OPS 設定，否則改用業主亂數代碼。
 * @purpose 封裝顯示值優先序規則，讓呼叫端可直接取得最終可渲染字串。
 * @external https://innotech.atlassian.net/browse/IN-124251
 */
export function resolveWalletConnectSignatureDisplayValue(ownerCode: string): string {
  const fromOps = typeof window !== 'undefined' ? (window._env_?.WALLET_CONNECT_DISPLAY_DOMAIN?.trim() ?? '') : '';
  if (fromOps.length > 0) {
    return fromOps;
  }
  return getRandomCodeByOwnerCode(ownerCode);
}

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:16:26.338Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note 已將檔案註解重構為三段式結構（檔案用途區塊／宣告內容用途說明與單號關聯／llm 分析紀錄區），並為各宣告補上符合規範的 @description、@purpose 與對應 Jira @external（使用完整 URL，且僅使用提供的 declarationOrigins 單號）。程式執行邏輯未變更。
 */
